/**
 * Copyright (C) 2019 Olivier Parisot - All Rights Reserved.
 */

import React from 'react';
import { Button, Modal, ModalBody, Container, Row, Col } from 'reactstrap';

import 'bootstrap/dist/css/bootstrap.min.css';

import gpx from 'gpx-for-runners';

import { Chart } from "react-google-charts";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowsAltH, faLevelUpAlt, faLevelDownAlt, faCaretUp } from '@fortawesome/free-solid-svg-icons'

import ColorHelper from './ColorHelper.js';

import RoutePlot from './RoutePlot.js';

import StarRatings from 'react-star-ratings';

import DBHelper from './DBHelper.js';

import ReactGA from 'react-ga';


/**
 * RouteModal.
 *
 * Author: Olivier Parisot
 */
class RouteModal extends React.Component 
{
  constructor(props) 
  {
    super(props);
    this.state =
    {
      forceClose: false,
      bestGPX: null,
      score: null,
      refGPX: null
    };
    this.close=this.close.bind(this);
  }

  getGPXObject(gpxString)
  {
    return new gpx(gpxString.split('</ele>').join('</ele><time>0</time>'));
  }

  loadGPX()
  {
    if (this.state.bestGPX===null)
    {
        const bestGPXPath=this.props.bestRoutePath.trim()+"best.gpx";
        fetch(bestGPXPath)
          .then(r =>
          {
            return r.text();
          })
          .then(data =>
          {
            var gpxObject=this.getGPXObject(data);
            gpxObject.path=bestGPXPath;
            this.setState({bestGPX: gpxObject});
          })
          .catch(error => 
          {
            console.error(error);
          });
    }
    if (this.state.score===null)
    {   
        const scorePath=this.props.bestRoutePath.trim()+"bestScore.json";
        fetch(scorePath)
          .then(r =>
          {
            return r.json();
          })
          .then(data =>
          {
            this.setState({score: data});
          })
          .catch(error => 
          {
            console.error(error);
          });
    }    
    if (this.state.refGPX===null)
    {
        const refGPXPath=this.props.refRoutePath+"ref.gpx";
        fetch(refGPXPath)
          .then(r =>
          {
            return r.text();
          })
          .then(data =>
          {
            var gpxObject=this.getGPXObject(data);
            gpxObject.path=refGPXPath;
            this.setState({refGPX: gpxObject});
          })
          .catch(error => 
          {
            console.error(error);
          });
    }
  }

  shouldComponentUpdate = (nextProps,nextState) =>
  {
    //console.info("RouteModal: shouldComponentUpdate "+this.props.bestRoutePath+" "+this.props.refRoutePath);
    if (this.props.bestRoutePath!==null)
    {
      this.loadGPX();
    } 
      
    return true;
  }

  prevRoute=null;

  static getDerivedStateFromProps = (props,state) =>
  {
    //console.info("RouteModal: getDerivedStateFromProps '"+props.bestRoutePath+"'");
    if (props.bestRoutePath !== this.prevRoute)
    {
      this.prevRoute=props.bestRoutePath;
      if (state.forceClose) return {forceClose: false, bestGPX: null, score: null, refGPX: null, prevRoute: null};
      else return {bestGPX: null, score: null, refGPX: null};
    }
    //console.info("ignored! "+state.forceClose);
    this.prevRoute=props.bestRoutePath;
    //if (props.opened&&this.state.forceClose) return {forceClose: false, bestGPX: null, score: null, refGPX: null, prevRoute: null};
    return {};
  }

  close() 
  {
    //console.info("close()");
    this.setState({forceClose: true});
  }

  titleCase = (str) =>
  {
       var words=str.toLowerCase().split(' ');
       for (var i=0;i<words.length;i++) 
       {
            var letters=words[i].split('');
            letters[0]=letters[0].toUpperCase();
            words[i]=letters.join('');
       }
       return words.join(' ');
  }

  getRouteChart = (gpxObj) =>
  {
    var dataset=[];
    var color='black';
    if (gpxObj!==null)
    {
      const coordsArray=gpxObj.trackpoints;
      for (var i=0;i<coordsArray.length;i++)  
      {
        dataset.push([coordsArray[i].lon,coordsArray[i].lat]);
      }
      color=ColorHelper.stringToColor(gpxObj.path);
    }
    return <RoutePlot points={dataset} color={color} />;
  }

  getElevationChart = (gpxObj) =>
  {
    var dataset=[['Dist','Elevation']];
    var color='black';
    if (gpxObj!==null)
    {
      const elevArray=gpxObj.elevation().elevation;
      for (var i=0;i<elevArray.length;i++) dataset.push([Number(elevArray[i].dist),Number(elevArray[i].elevation)]);
      color=ColorHelper.stringToColor(gpxObj.path);
    }
    return (
            <Chart
              chartType="AreaChart"
              loader={<div style={{height: window.innerHeight/5}}>Loading ...</div>}
              data={dataset}
              options={{
                colors: [color],
                vAxis: { baseLineColor: 'none', gridlines: {color: 'none'}, minValue: gpxObj!==null?gpxObj.elevation().min:0, maxValue: gpxObj!==null?Math.max(50,gpxObj.elevation().max):0 },
                legend :'none', 
                hAxis: { baseLineColor: 'none', gridlines: {color: 'none'}, textPosition: 'none' },
                chartArea: {width: '100%', height: '100%'}
              }}
            />
    );
  }

  getStatsDiv = (gpxObj) =>
  {
    return (
        <span>
           <FontAwesomeIcon icon={faArrowsAltH} />&nbsp;{gpxObj!==null?"Distance: "+Number(gpxObj.distance()).toFixed(2)+"m":"n/a"}
           &nbsp;&nbsp;&nbsp;
           <FontAwesomeIcon icon={faLevelUpAlt} />&nbsp;{gpxObj!==null?"Elevation gain: "+Number(gpxObj.elevation().gain).toFixed(0)+"m":"n/a"}
           &nbsp;&nbsp;&nbsp;
           <FontAwesomeIcon icon={faLevelDownAlt} />&nbsp;{gpxObj!==null?"Elevation loss: "+Number(gpxObj.elevation().loss).toFixed(0)+"m":"n/a"}
           &nbsp;&nbsp;&nbsp;
           <FontAwesomeIcon icon={faCaretUp} />&nbsp;{gpxObj!==null?"Start altitude: "+Number(gpxObj.elevation().elevation[0].elevation).toFixed(0)+"m":"n/a"}
        </span>
    );
  }

  render() 
  {
    if (this.props.bestRoutePath===null) return (<br/>);
  
    //console.info("BEST: "+this.props.bestRoutePath);
    //const refName=this.props.bestRoutePath.substring(DBHelper.rootUrl.length).split('/')[2];
    const refName=this.props.bestRoutePath.split('\\')[2];
    const nearestCity=this.props.bestRoutePath.split('\\')[1];
    
    const forunrLink="https://www.forunr.com//result.html?initParam="+nearestCity.replace(/"/g, '&quot;').replace(/'/g, '%27')+"&refParam="+refName;
    const permaLink=this.props.permaLink;

    var component=(<br/>);
    if (!DBHelper.flat)
    {
      component=(
      <div>
      <hr/>
      <h5>... to train for {this.titleCase(refName.split(".")[0].replace("-"," ").replace("-"," "))}</h5>
      {this.getStatsDiv(this.state.refGPX)}
      <br/> <br/> 
      <Container>
        <Row>
          <Col xs='6'>{this.getRouteChart(this.state.refGPX)}</Col>
          <Col xs='6'>{this.getElevationChart(this.state.refGPX)}</Col>
        </Row>
      </Container>
      </div>
      );
    }

    return (
      <div>
        <Modal returnFocusAfterClose={true} fade={false} size="xl" isOpen={!this.state.forceClose&&this.props.bestRoutePath!==null}  >
          <ModalBody className="App-Modal">
           <Button close onClick={this.close}/>
           <h5>You can run on this route near {nearestCity} ...</h5>
           {this.getStatsDiv(this.state.bestGPX)}
           <br/> 
           <Container>
             <Row>
               <Col xs='6'>{this.getRouteChart(this.state.bestGPX)}</Col>
               <Col xs='6'>{this.getElevationChart(this.state.bestGPX)}</Col>
             </Row>
           </Container>
           <br/>
           <hr/> 
            <Container>
              <Row>
                <Col>
                  <StarRatings starDimension="30px" starRatedColor="YELLOW" rating={this.state.score !== null ? (this.state.score.fullScore - 80) / 4 : 0} />
                </Col>
                <Col>
                  <span>
                    <a onClick={(e)=>{ReactGA.event({category:'permaLink',action:'permaLink'});}} href={this.state.bestGPX !== null ? permaLink : ''} target='_blank' rel="noopener noreferrer">
                      <Button outline color="secondary" size="sm">Permalink</Button>
                    </a>
                    &nbsp;
                    &nbsp;
                    <a onClick={(e)=>{ReactGA.event({category:'permaLink',action:'permaLink'});}} href={this.state.bestGPX !== null ? forunrLink : ''} target='_blank' rel="noopener noreferrer">
                      <Button outline color="secondary" size="sm">Other view</Button>
                    </a>
                    &nbsp;
                    &nbsp;
                    <a onClick={(e)=>{ReactGA.event({category:'downloadGPX',action:'downloadGPX'});}} href={this.state.bestGPX !== null ? this.state.bestGPX.path : ''} target='_blank' rel="noopener noreferrer">
                      <Button color="secondary" size="sm">Download GPX (*)</Button>
                    </a>
                    &nbsp;
                   <small className="text-muted">
                      <i>(*) Licensed under&nbsp;
                        <a href="http://creativecommons.org/licenses/by-nc-sa/4.0/" target="_blank" rel="noopener noreferrer">CC BY-NC-SA 4.0</a>
                      </i>
                    </small>
                  </span>
                </Col>
              </Row>
            </Container>           
            {component}
          </ModalBody>
        </Modal>
      </div>
    );
  }
}

export default RouteModal;
